import type { ReactNode } from "react";
import type { TableCellProps } from "./TableCell";
import { TableCell } from "./TableCell";
import type { TableCellHeaderProps } from "./TableCellHeader";
import { TableCellHeader } from "./TableCellHeader";
import { meterToFeet, perSqmToPerHectare, perSqmToPerSqft, sqmToSqft } from "src/hooks/meterToFeet";
import type { RegisteredColumnId } from "./RegisteredColumnId";
import {
  RegisteredColumnId_Base,
  RegisteredColumnId_Cost,
  RegisteredColumnId_Design,
  RegisteredColumnId_Sustainability,
} from "./RegisteredColumnId";
import { observer } from "mobx-react-lite";
import { useTranslation } from "src/translations";
import { columnLabel } from "./columnLabel";
import type { RowData } from "./RowData";
import { UnitMixCell } from "./UnitMixCell";
import { useActiveParams } from "src/hooks/useActiveParams";
import { UnitCategory, UnitType, unitSuffix } from "src/types/UnitType";
import { useCurrencySymbol } from "src/hooks/useCurrencySymbol";

export const enum SortDirection {
  Asc = "ascending",
  Desc = "descending",
}

interface RenderCellProps extends TableCellProps {
  data: RowData;
  unitType: UnitType;
  hover?: boolean;
  selected?: boolean;
  unitCategory: UnitCategory | null;
  onCellSelect?: ((id: string) => void) | undefined;
}

interface RenderHeadProps<K extends RegisteredColumnId> extends TableCellHeaderProps<K> {
  unitType: UnitType;
  unitCategory: UnitCategory | null;
}

type RenderCell = (props: RenderCellProps) => ReactNode;

type RenderHead<K extends RegisteredColumnId> = (props: RenderHeadProps<K>) => ReactNode;

interface SortByProps {
  sortedColumn: SortDirection | null;
}

type SortBy = (a: RowData, b: RowData, props: SortByProps) => number;

export interface DesignColumn<K extends RegisteredColumnId = RegisteredColumnId> {
  id: K;
  label: string;
  defaultWidth: number;
  unitCategory: UnitCategory | null;
  resizable: boolean;
  RenderHead: RenderHead<K>;
  RenderCell: RenderCell;
  sortBy?: SortBy;
}

export const RegisteredColumns: { [K in RegisteredColumnId]: DesignColumn<K> } = {
  IMAGE: {
    label: "IMAGE",
    id: RegisteredColumnId_Base.IMAGE,
    resizable: true,
    defaultWidth: 56,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      return <TableCellHeader {...props} />;
    }),
    RenderCell: observer((props) => {
      const { projectId, scenarioId } = useActiveParams();
      if (!props.data.thumbnail) return null;
      return (
        <TableCell {...props}>
          <div
            className="border-neutral-4 border rounded-sm bg-neutral-4 w-[32px] h-[32px]"
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              if (!projectId || !scenarioId) return;
              props.onCellSelect?.(props.data.id);
            }}
            style={{
              backgroundImage: `url("${props.data.thumbnail}")`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />
        </TableCell>
      );
    }),
  },
  NAME: {
    label: "NAME",
    id: RegisteredColumnId_Base.NAME,
    resizable: true,
    defaultWidth: 136,
    unitCategory: null,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return <TableCellHeader {...props}>{columnLabel(props.id, "Name", t)}</TableCellHeader>;
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">{props.data.label}</div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.label),
  },
  UNITS: {
    label: "Units",
    id: RegisteredColumnId_Design.UNITS,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return <TableCellHeader {...props}>{columnLabel(props.id, "Units", t)}</TableCellHeader>;
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.ApartmentQuantityTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.ApartmentQuantityTotal),
  },
  RESIDENTIALEFFICIENCY: {
    label: "Residential efficiency",
    id: RegisteredColumnId_Design.RESIDENTIALEFFICIENCY,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Percentage,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Residential Efficiency", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>
              {(props.data.architecturalMetrics.ResidentialEfficiencyTotal * 100).toFixed(1)}
            </span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.ResidentialEfficiencyTotal),
  },
  GIA: {
    label: "GIA",
    id: RegisteredColumnId_Design.GIA,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "GIA", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.GIATotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.GIATotal),
  },
  NIA: {
    label: "NIA",
    id: RegisteredColumnId_Design.NIA,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "NIA", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.NIATotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.NIATotal),
  },
  CONSTRUCTIONCOST: {
    label: "Construction cost",
    id: RegisteredColumnId_Cost.CONSTRUCTIONCOST,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Construction Cost", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span
            data-testid="cost-construction-cell"
            data-construction-cost={String(props.data.costMetrics.summary.construction)}
          >
            <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
            {formatNumber(props.data.costMetrics.summary.construction, 0)}
          </span>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.construction),
  },
  CO2FOOTPRINT: {
    label: "CO2 footprint",
    id: RegisteredColumnId_Sustainability.CO2FOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Mass,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full items-center space-x-1">
            <span>{columnLabel(props.id, "CO2 Footprint", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatNumber(props.data.sustainabilityMetrics.summary.total, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.summary.total),
  },
  UNITMIX: {
    label: "Unit mix",
    id: RegisteredColumnId_Design.UNITMIX,
    resizable: false,
    defaultWidth: 119,
    unitCategory: UnitCategory.Percentage,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-start w-full space-x-1">
            <span>{columnLabel(props.id, "Unit Mix", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <UnitMixCell
          type={props.type}
          width={props.width}
          hover={props.hover}
          selected={props.selected}
          data={props.data}
        />
      );
    }),
  },
  BUILDINGS: {
    label: "Buildings",
    id: RegisteredColumnId_Design.BUILDINGS,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Buildings", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.buildingsCount}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.buildingsCount),
  },
  MODULES: {
    label: "Modules",
    id: RegisteredColumnId_Design.MODULES,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Modules", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.ModuleQuantityTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.ModuleQuantityTotal),
  },
  FLOORS: {
    label: "Floors",
    id: RegisteredColumnId_Design.FLOORS,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Floors", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">{props.data.architecturalMetrics.FloorMax}</div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FloorMax),
  },
  PODIUMLEVELS: {
    label: "Podium level(s)",
    id: RegisteredColumnId_Design.PODIUMLEVELS,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Podium Levels", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.PodiumLevelCount}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.PodiumLevelCount),
  },
  FLOORSRESIDENTIAL: {
    label: "Floors (residential)",
    id: RegisteredColumnId_Design.FLOORSRESIDENTIAL,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Floors (residential)", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.ResidentialLevelCountMax}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FloorMax),
  },
  ENTRANCES: {
    label: "Entrances",
    id: RegisteredColumnId_Design.ENTRANCES,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Entrances", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.EntrancesTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.EntrancesTotal),
  },
  CORES: {
    label: "Cores",
    id: RegisteredColumnId_Design.CORES,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Cores", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.CoresTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.CoresTotal),
  },
  FIREFIGHTINGCORES: {
    label: "Firefighting cores",
    id: RegisteredColumnId_Design.FIREFIGHTINGCORES,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Firefighting Cores", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.FireCoresTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FireCoresTotal),
  },
  LIFTCORES: {
    label: "Lift cores",
    id: RegisteredColumnId_Design.LIFTCORES,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Lift Cores", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.LiftCoresTotal}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.LiftCoresTotal),
  },
  BUILDINGHEIGHT: {
    label: "Building height",
    id: RegisteredColumnId_Design.BUILDINGHEIGHT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Length,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Building Height", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatLength(props.data.architecturalMetrics.BuildingHeightMax, props.unitType, 2)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.BuildingHeightMax),
  },
  FFL: {
    label: "FFL",
    id: RegisteredColumnId_Design.FFL,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Length,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "FFL", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatLength(props.data.architecturalMetrics.FFLMax, props.unitType, 2)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FFLMax),
  },
  GEA: {
    label: "GEA",
    id: RegisteredColumnId_Design.GEA,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "GEA", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.GEATotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.GEATotal),
  },
  BUILDINGFOOTPRINT: {
    label: "Building footprint",
    id: RegisteredColumnId_Design.BUILDINGFOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Building Footprint", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.FootPrintTotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FootPrintTotal),
  },
  COMMERCIALAREA: {
    label: "Commercial area",
    id: RegisteredColumnId_Design.COMMERCIALAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Commercial Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(
              props.data.architecturalMetrics.CommercialAreaPerLevel.at(0) ?? 0,
              props.unitType,
              0
            )}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.CommercialAreaPerLevel.at(0) ?? 0),
  },
  NONRESIDENTIALAREA: {
    label: "Non-residential area",
    id: RegisteredColumnId_Design.NONRESIDENTIALAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Non Residential Area", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.NonResidentialAreaTotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.NonResidentialAreaTotal),
  },
  COREANDCORRIDOR: {
    label: "Core & corridor area",
    id: RegisteredColumnId_Design.COREANDCORRIDOR,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Core & Corridor Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(
              props.data.architecturalMetrics.CoreAndCorridorAreaTotal,
              props.unitType,
              0
            )}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.CoreAndCorridorAreaTotal),
  },
  SERVICEAREA: {
    label: "Services area",
    id: RegisteredColumnId_Design.SERVICEAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Service Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(
              props.data.architecturalMetrics.ServiceAreaPerLevel.at(0) ?? 0,
              props.unitType,
              0
            )}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.ServiceAreaPerLevel.at(0) ?? 0),
  },
  AMENITIESAREA: {
    label: "Amenities area",
    id: RegisteredColumnId_Design.AMENITIESAREA,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Amenities Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.AmenitiesAreaTotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.AmenitiesAreaTotal),
  },
  GROSSFACADEAREA: {
    label: "Gross façade area",
    id: RegisteredColumnId_Design.GROSSFACADEAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Gross Façade Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.FacadeTotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FacadeTotal),
  },
  GLAZEDFACADEAREA: {
    label: "Glazed façade area",
    id: RegisteredColumnId_Design.GLAZEDFACADEAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Glazed Façade Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.GlazedFacade, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.GlazedFacade),
  },
  NETFACADEAREA: {
    label: "Net façade area",
    id: RegisteredColumnId_Design.NETFACADEAREA,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Net Façade Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.FacadePerLevel, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FacadePerLevel),
  },
  ROOFAREA: {
    label: "Roof area",
    id: RegisteredColumnId_Design.ROOFAREA,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Area,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Roof Area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.RoofAreaTotal, props.unitType, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.RoofAreaTotal),
  },
  DENSITY: {
    label: "Density",
    id: RegisteredColumnId_Design.DENSITY,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.CountPerHectare,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Density", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatSqmPerHectare(props.data.architecturalMetrics.DensityUnitsTotal, 2)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.DensityUnitsTotal),
  },
  DENSITY_HABITABLEROOMS: {
    label: "Density (habitable rooms)",
    id: RegisteredColumnId_Design.DENSITY_HABITABLEROOMS,
    resizable: true,
    defaultWidth: 250,
    unitCategory: UnitCategory.CountPerHectare,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Density (habitable rooms)", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatSqmPerHectare(props.data.architecturalMetrics.DensityHabitableRoomsTotal, 2)}
          </div>
        </TableCell>
      );
    }),
    sortBy: (a, b, props) => {
      if (!props.sortedColumn) return 0;
      const first = props.sortedColumn === SortDirection.Asc ? a : b;
      const second = first === a ? b : a;
      return first.architecturalMetrics.NIATotal - second.architecturalMetrics.NIATotal;
    },
  },
  BUILDINGEFFICIENCY: {
    label: "Building efficiency",
    id: RegisteredColumnId_Design.BUILDINGEFFICIENCY,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Percentage,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Building Efficiency", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>
              {(props.data.architecturalMetrics.BuildingEfficiencyTotal * 100).toFixed(1)}
            </span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.BuildingEfficiencyTotal),
  },
  LANDCOVERAGE: {
    label: "Land coverage",
    id: RegisteredColumnId_Design.LANDCOVERAGE,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Percentage,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Land Coverage", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{(props.data.architecturalMetrics.LandCoverageTotal * 100).toFixed(1)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.LandCoverageTotal),
  },
  NORTHFACINGUNITS: {
    label: "North facing units",
    id: RegisteredColumnId_Design.NORTHFACINGUNITS,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "North Facing Units", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{props.data.architecturalMetrics.NorthFacingUnitsTotal}</span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.NorthFacingUnitsTotal),
  },
  PARTMCOMPLIANTUNIT: {
    label: "(*) Part M compliant units",
    id: RegisteredColumnId_Design.PARTMCOMPLIANTUNIT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Part M Compliant Unit (*)", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">**</div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.NorthFacingUnitsTotal),
  },
  UNITSPERCORE: {
    label: "Unit per core",
    id: RegisteredColumnId_Design.UNITSPERCORE,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Units per Core", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{props.data.architecturalMetrics.UnitToLiftCoreRatio.toFixed(0)}</span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.UnitToLiftCoreRatio),
  },
  DUALASPECT: {
    label: "(*) Dual aspect units",
    id: RegisteredColumnId_Design.DUALASPECT,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Count,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Dual Aspect (*)", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">**</div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.UnitToLiftCoreRatio),
  },

  MODULESCOST: {
    label: "Modules",
    id: RegisteredColumnId_Cost.MODULESCOST,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Modules", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.constructionModules, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.constructionModules),
  },
  FITOUT: {
    label: "Fit-out",
    id: RegisteredColumnId_Cost.FITOUT,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Fit-out", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.constructionFitOut, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.constructionFitOut),
  },
  BASEBUILD: {
    label: "Base build",
    id: RegisteredColumnId_Cost.BASEBUILD,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Base Build", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.constructionBaseBuild, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.constructionBaseBuild),
  },
  PRELIMS: {
    label: "Prelims",
    id: RegisteredColumnId_Cost.PRELIMS,
    resizable: true,
    defaultWidth: 120,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Prelims", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.preliminaries, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.preliminaries),
  },
  TOTALCOST: {
    label: "Total cost",
    id: RegisteredColumnId_Cost.TOTALCOST,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Total Cost", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.total, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.total),
  },
  CONTINGENCY: {
    label: "Contingency",
    id: RegisteredColumnId_Cost.CONTINGENCY,
    resizable: true,
    defaultWidth: 150,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Contingency", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.contingencies, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.contingencies),
  },
  OHAPALLOWANCE: {
    label: "OH&P allowance",
    id: RegisteredColumnId_Cost.OHAPALLOWANCE,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Cost,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "OH & P Allowance", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{unitSuffix(props.unitType, props.unitCategory)}</span>
          {formatNumber(props.data.costMetrics.summary.overheadsAndProfits, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.summary.overheadsAndProfits),
  },
  CONSTRUCTIONSQFT: {
    label: "Construction /area",
    id: RegisteredColumnId_Cost.CONSTRUCTIONSQFT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.CostPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Construction /area", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      const currency = useCurrencySymbol();
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{currency}</span>
          {formatPerSqmPerSqft(
            props.data.costMetrics.perMetrics.constructionPerArea,
            props.unitType,
            0
          )}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.perMetrics.constructionPerArea),
  },
  CONSTRUCTIONUNIT: {
    label: "Construction /unit",
    id: RegisteredColumnId_Cost.CONSTRUCTIONUNIT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.CostPerUnit,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Construction /unit", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      const currency = useCurrencySymbol();
      return (
        <TableCell {...props}>
          <span className="text-neutral-6">{currency}</span>
          {formatNumber(props.data.costMetrics.perMetrics.constructionPerUnit, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.costMetrics.perMetrics.constructionPerUnit),
  },
  INDICATIVEFORMFACTOR: {
    label: "Indicative form factor",
    id: RegisteredColumnId_Design.INDICATIVEFORMFACTOR,
    resizable: true,
    defaultWidth: 200,
    unitCategory: null,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Indicative form factor", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {props.data.architecturalMetrics.IndicativeFormFactorTotal.toFixed(3)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.IndicativeFormFactorTotal),
  },
  WALLTOFLOORRATIO: {
    label: "Wall to floor ratio",
    id: RegisteredColumnId_Design.WALLTOFLOORRATIO,
    resizable: true,
    defaultWidth: 200,
    unitCategory: null,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full">{columnLabel(props.id, "Wall to floor ratio", t)}</div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatArea(props.data.architecturalMetrics.WallToFloorRatioTotal, props.unitType, 2)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.WallToFloorRatioTotal),
  },
  CONCAVEFACADECORNER: {
    label: "Concave façade corner length",
    id: RegisteredColumnId_Design.CONCAVEFACADECORNER,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Length,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Concave Façade Corner Length", t)}</span>{" "}
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>
              {formatLength(
                props.data.architecturalMetrics.FacadeInternalCornerLength,
                props.unitType,
                2
              )}
            </span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FacadeInternalCornerLength),
  },
  CONVEXFACADECORNER: {
    label: "Convex façade corner length",
    id: RegisteredColumnId_Design.CONVEXFACADECORNER,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Length,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Convex Façade Corner Length", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>
              {formatLength(
                props.data.architecturalMetrics.FacadeExternalCornerLength,
                props.unitType,
                2
              )}
            </span>
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.FacadeExternalCornerLength),
  },
  SUBSTRUCTURECO2FOOTPRINT: {
    label: "Substructure CO2 footprint /area",
    id: RegisteredColumnId_Sustainability.SUBSTRUCTURECO2FOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.MassPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end space-x-1">
            <span>{columnLabel(props.id, "Substructure CO2 Footprint /area", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatArea(props.data.sustainabilityMetrics.perMetrics.substructure, props.unitType, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.perMetrics.substructure),
  },
  SUPERSTRUCTURECO2FOOTPRINT: {
    label: "Superstructure CO2 footprint /area",
    id: RegisteredColumnId_Sustainability.SUPERSTRUCTURECO2FOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.MassPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Superstructure CO2 Footprint /area", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatArea(
            props.data.sustainabilityMetrics.perMetrics.superstructure,
            props.unitType,
            0
          )}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.perMetrics.superstructure),
  },
  STEELCO2FOOTPRINT: {
    label: "Steel CO2 footprint /area ",
    id: RegisteredColumnId_Sustainability.STEELCO2FOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.MassPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Steel CO2 Footprint /area", t)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatPerSqmPerSqft(
              props.data.sustainabilityMetrics.perMetrics.steel,
              props.unitType,
              0
            )}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.perMetrics.steel),
  },
  STEELCO2FOOTPRINT2: {
    label: "Steel CO2 footprint ",
    id: RegisteredColumnId_Sustainability.STEELCO2FOOTPRINT2,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Mass,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Steel CO2 Footprint", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatNumber(props.data.architecturalMetrics.SteelCO2footprint, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.SteelCO2footprint),
  },
  CONCRETECO2FOOTPRINT: {
    label: "Concrete CO2 footprint /area",
    id: RegisteredColumnId_Sustainability.CONCRETECO2FOOTPRINT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.MassPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Concrete CO2 footprint /area", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatPerSqmPerSqft(
              props.data.sustainabilityMetrics.perMetrics.concrete,
              props.unitType,
              0
            )}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.perMetrics.concrete),
  },
  CONCRETECO2FOOTPRINT2: {
    label: "Concrete CO2 footprint",
    id: RegisteredColumnId_Sustainability.CONCRETECO2FOOTPRINT2,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Mass,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Concrete CO2 Footprint", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          <div className="flex justify-end w-full">
            {formatNumber(props.data.architecturalMetrics.ConcreteCO2footprint, 0)}
          </div>
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.architecturalMetrics.ConcreteCO2footprint),
  },
  SUBSTRUCTURECO2FOOTPRINT2: {
    label: "Substructure CO2 footprint",
    id: RegisteredColumnId_Sustainability.SUBSTRUCTURECO2FOOTPRINT2,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Mass,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Substructure CO2 Footprint", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatNumber(props.data.sustainabilityMetrics.summary.substructure, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.summary.substructure),
  },
  SUPERSTRUCTURECO2FOOTPRINT2: {
    label: "Superstructure CO2 footprint",
    id: RegisteredColumnId_Sustainability.SUPERSTRUCTURECO2FOOTPRINT2,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.Mass,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "Super Structure CO2Footprint", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatNumber(props.data.sustainabilityMetrics.summary.superstructure, 0)}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.summary.superstructure),
  },
  CO2FOOTPRINTSQFT: {
    label: "CO2 footprint /area",
    id: RegisteredColumnId_Sustainability.CO2FOOTPRINTSQFT,
    resizable: true,
    defaultWidth: 200,
    unitCategory: UnitCategory.MassPerArea,
    RenderHead: observer((props) => {
      const { t } = useTranslation();
      return (
        <TableCellHeader {...props}>
          <div className="flex justify-end w-full space-x-1">
            <span>{columnLabel(props.id, "CO2 Footprint /area", t)}</span>
            <span className="text-body-1">{unitSuffix(props.unitType, props.unitCategory)}</span>
          </div>
        </TableCellHeader>
      );
    }),
    RenderCell: observer((props) => {
      return (
        <TableCell {...props}>
          {formatPerSqmPerSqft(
            props.data.sustainabilityMetrics.perMetrics.total,
            props.unitType,
            0
          )}
        </TableCell>
      );
    }),
    sortBy: sort((row) => row.sustainabilityMetrics.perMetrics.total),
  },
};

function formatSqmPerHectare(num: number, fractionDigits: number) {
  return perSqmToPerHectare(num).toLocaleString("en", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatNumber(num: number, fractionDigits: number) {
  return num.toLocaleString("en", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatLength(num: number, lengthUnits: UnitType, fractionDigits: number) {
  const value = lengthUnits === UnitType.Metric ? num : meterToFeet(num);
  return value.toLocaleString("en", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatArea(num: number, areaUnits: UnitType, fractionDigits: number) {
  const value = areaUnits === UnitType.Metric ? num : sqmToSqft(num);
  return value.toLocaleString("en", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}
function formatPerSqmPerSqft(num: number, areaUnits: UnitType, fractionDigits: number) {
  const value = areaUnits === UnitType.Metric ? num : perSqmToPerSqft(num);
  return value.toLocaleString("en", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function sort<T extends number | string>(getValue: (row: RowData) => T): SortBy {
  return (a, b, props) => {
    if (!props.sortedColumn) return 0;
    const first = props.sortedColumn === SortDirection.Asc ? a : b;
    const second = first === a ? b : a;
    return compareStrings(getValue(first), getValue(second));
  };
}

// Define a custom comparison function that respects embedded numbers.
function compareStrings(a: string | number, b: string | number) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "number") a = a.toString();
  if (typeof b === "number") b = b.toString();
  const regex = /\d+/g;
  const aNumbers = a.match(regex)?.map(Number) || [];
  const bNumbers = b.match(regex)?.map(Number) || [];
  for (let i = 0; i < Math.max(aNumbers.length, bNumbers.length); i++) {
    const numA = aNumbers[i] || 0;
    const numB = bNumbers[i] || 0;
    if (numA !== numB) {
      return numA - numB;
    }
  }
  return a.localeCompare(b); // If numbers are equal, compare alphabetically.
}
